import React from 'react';
import { observer } from 'mobx-react';
import { FieldProps } from './types';
import { FormBuilderContext } from '../formBuilderContext';
import { FormEntryNew } from '../../formEntryNew';
import { FieldWrapper } from './fieldWrapper';
import { AntSelect } from '../../../ant/ant-select/antSelect';
import { AntInputNumber } from '../../../ant/antInputNumber';
import { translator } from 'core/localization/localization';
import { AlgorithmOperator, AlgorithmTypeOption, AlgorithmValue, defaultAlgorithmValue } from '../models';
import { fieldNamesTextValue } from 'controls/react/ant/ant-select/types';

const i18n = translator({
	'Operator': { en: 'Operator', no: 'Operator' },
	'Threshold': { en: 'Threshold', no: 'Terskelverdi' },
	'Factor': { en: 'Factor', no: 'Faktor' },
	'Min': { en: 'Min', no: 'Min' },
	'Max': { en: 'Max', no: 'Maks' },
	'Unit': { en: 'Unit', no: 'Enhet' },
	'Percentile': { en: 'Percentile', no: 'Persentil' },
	'Days': { en: 'Days', no: 'Dager' },
	'Equal': { en: 'Equal', no: 'Lik' },
	'Greater or equal than': { en: 'Greater or equal than', no: 'Større eller lik' },
	'Greater than': { en: 'Greater than', no: 'Større enn' },
	'Less or equal than': { en: 'Less or equal than', no: 'Mindre eller lik' },
	'Less than': { en: 'Less than', no: 'Mindre enn' },
	'Not equal': { en: 'Not equal', no: 'Ikke lik' },
});

export const OPERATORS: { text: string; value: AlgorithmOperator }[] = [
	{ text: i18n('Equal'), value: 'EQ' },
	{ text: i18n('Greater or equal than'), value: 'GE' },
	{ text: i18n('Greater than'), value: 'GT' },
	{ text: i18n('Less or equal than'), value: 'LE' },
	{ text: i18n('Less than'), value: 'LT' },
	{ text: i18n('Not equal'), value: 'NE' },
];

// type -> which sub-fields to show, ported from the old ceAlgorithm.js switch statement
function fieldsForType(type: string) {
	switch (type) {
		case 'StaticThresholdAlgorithm':
		case 'DeltaAlgorithm':
			return { operator: true, threshold: true };
		case 'DynamicThresholdAlgorithm':
			return { factor: true };
		case 'RangeAlgorithm':
			return { range: true };
		case 'PercentileAlgorithm':
			return { percentile: true, days: true };
		default:
			return {};
	}
}

export const CEAlgorithm = observer(({ property }: FieldProps) => {
	const store = React.useContext(FormBuilderContext);
	const data = (store.dataSources.get(property.id) as AlgorithmTypeOption[]) || [];
	const value = store.value[property.id] as AlgorithmValue;
	const disabled = store.propertyDisabled(property);

	if (!data.length || !value) return null;

	const currentTypeData = data.find((x) => x.value === value.type);
	const unitList = currentTypeData?.unitList ?? [];
	const fields = fieldsForType(value.type ?? '');

	const onTypeChange = (type: string) => {
		store.onPropertyValueChange(property, defaultAlgorithmValue(type));
	};

	return (
		<>
			<FieldWrapper property={property} onChange={onTypeChange} value={value.type}>
				<AntSelect options={data} fieldNames={fieldNamesTextValue} disabled={disabled} />
			</FieldWrapper>
			{fields.operator && (
				<FormEntryNew label={i18n('Operator')} model={value} modelField={'operator'} disabled={disabled}>
					<AntSelect options={OPERATORS} fieldNames={fieldNamesTextValue} disabled={disabled} />
				</FormEntryNew>
			)}
			{fields.threshold && (
				<>
					<FormEntryNew label={i18n('Threshold')} model={value} modelField={'threshold'} disabled={disabled}>
						<AntInputNumber disabled={disabled} />
					</FormEntryNew>
					{!!unitList.length && (
						<FormEntryNew label={i18n('Unit')} model={value} modelField={'thresholdUnit'} disabled={disabled}>
							<AntSelect options={unitList} fieldNames={fieldNamesTextValue} disabled={disabled} />
						</FormEntryNew>
					)}
				</>
			)}
			{fields.factor && (
				<FormEntryNew label={i18n('Factor')} model={value} modelField={'factor'} disabled={disabled}>
					<AntInputNumber min={1} max={10} disabled={disabled} />
				</FormEntryNew>
			)}
			{fields.range && (
				<>
					<FormEntryNew label={i18n('Min')} model={value} modelField={'min'} disabled={disabled}>
						<AntInputNumber disabled={disabled} />
					</FormEntryNew>
					<FormEntryNew label={i18n('Max')} model={value} modelField={'max'} disabled={disabled}>
						<AntInputNumber disabled={disabled} />
					</FormEntryNew>
					{!!unitList.length && (
						<FormEntryNew label={i18n('Unit')} model={value} modelField={'thresholdUnit'} disabled={disabled}>
							<AntSelect options={unitList} fieldNames={fieldNamesTextValue} disabled={disabled} />
						</FormEntryNew>
					)}
				</>
			)}
			{fields.percentile && (
				<FormEntryNew
					label={i18n('Percentile')}
					model={value}
					modelField={'percentile'}
					validator={store.validator.modelValidator}
					validatorField={`${property.id}.percentile`}
					disabled={disabled}
				>
					<AntInputNumber disabled={disabled} />
				</FormEntryNew>
			)}
			{fields.days && (
				<FormEntryNew
					label={i18n('Days')}
					model={value}
					modelField={'days'}
					validator={store.validator.modelValidator}
					validatorField={`${property.id}.days`}
					disabled={disabled}
				>
					<AntInputNumber min={1} disabled={disabled} />
				</FormEntryNew>
			)}
		</>
	);
});
